import React, { createContext, useContext, useState, useEffect, useMemo, useRef, useCallback } from 'react';

const LocationPermissionContext = createContext();


export const useLocationPermission = () => {
  const context = useContext(LocationPermissionContext);
  // Trigger lazy fetch when a consumer uses this hook
  if (context?.ensureFetched) context.ensureFetched();
  return context;
};

export const LocationPermissionProvider = ({ children }) => {
  // enabled: true | false | null (unknown)
  const [enabled, setEnabled] = useState(null);
  const hasFetchedRef = useRef(false);

  const fetchSettings = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const apiUrl = process.env.REACT_APP_API_URL;
      const resp = await fetch(`${apiUrl}/user/notification-settings/get`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (!resp.ok) throw new Error('settings-fetch-failed');
      const json = await resp.json();
      const enabledVal = json?.settings?.currentlocation?.enabled;
      if (typeof enabledVal === 'boolean') {
        setEnabled(enabledVal);
      } else if (enabledVal === 1 || enabledVal === '1' || enabledVal === 'true') {
        setEnabled(true);
      } else if (enabledVal === 0 || enabledVal === '0' || enabledVal === 'false') {
        setEnabled(false);
      } else {
        // New users have no saved settings yet — default to true
        // (matches Notification.js initial formik value for currentlocation)
        setEnabled(true);
      }
    } catch (err) {
      // On error (e.g. new user with no settings), default to true
      setEnabled(true);
    }
  }, []);

  // Eager fetch — start immediately when token exists so result is ready
  // before Portfolio mounts (eliminates sequential wait)
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token && !hasFetchedRef.current) {
      hasFetchedRef.current = true;
      fetchSettings();
    }
  }, [fetchSettings]);

  // Also allow manual trigger (keeps backward compatibility)
  const ensureFetched = useCallback(() => {
    if (!hasFetchedRef.current) {
      hasFetchedRef.current = true;
      fetchSettings();
    }
  }, [fetchSettings]);

  const contextValue = useMemo(() => ({ enabled, setEnabled, ensureFetched }), [enabled, ensureFetched]);

  return (
    <LocationPermissionContext.Provider value={contextValue}>
      {children}
    </LocationPermissionContext.Provider>
  );
};

export default LocationPermissionContext;
